import type { Product } from '@frontend/libs/types/product'

import VideoPlayer from '@frontend/components/video-player/VideoPlayer'
import { LOAD_MORE_OFFSET } from '@frontend/libs/constants'
import { useUserStore } from '@frontend/libs/stores/useUserStore'
import { ImageSize } from '@frontend/libs/types/media'
import { isMobileDevice } from '@frontend/libs/utils'
import { ArchiveBoxIcon } from '@heroicons/react/24/solid'
import { cn } from '@nextui-org/react'
import { endpoint } from '@shared/services/endpoint'
import { getRequest } from '@shared/services/fetcher'
import { createURLParams } from '@shared/utils/create-url-params'
import { __ } from '@wordpress/i18n'
import _ from 'lodash'
import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import useSWR from 'swr'

import ProductItem from '../products/components/product-item/ProductItem'
import ShopLoader from '../shop/components/ShopLoader'
import DrawerFilter from './components/DrawerFilter'
import LookBookSlider from './components/LookBookSlider'
import ProductListFilter, {
  type ProductListFilterProps,
} from './components/ProductListFilter'
import { type LookBook, type ProductListResponse, Sort } from './libs/type'
import {
  type SelectedFilters,
  useProductListStore,
} from './libs/useProductListStore'

export function setFilterToSearchParams(selectedFilters: SelectedFilters) {
  const params = new URLSearchParams()

  if (selectedFilters.search) {
    params.set('s', selectedFilters.search)
  }

  if (selectedFilters.brands.length > 0) {
    params.set('brands', selectedFilters.brands.join(','))
  }

  if (selectedFilters.categories.length > 0) {
    params.set('categories', selectedFilters.categories.join(','))
  }

  if (selectedFilters.collections.length > 0) {
    params.set('collections', selectedFilters.collections.join(','))
  }

  if (selectedFilters.discounts.length > 0) {
    params.set('discounts', selectedFilters.discounts.join(','))
  }

  if (selectedFilters.colors.length > 0) {
    params.set('colors', selectedFilters.colors.join(','))
  }

  if (selectedFilters.sizes.length > 0) {
    params.set('sizes', selectedFilters.sizes.join(','))
  }

  if (selectedFilters.sort) {
    params.set('sort', selectedFilters.sort)
  }

  return params
}

export function getFilterFromSearchParams(searchParams: URLSearchParams) {
  const search = searchParams.get('s')
  const brands = searchParams.get('brands')
  const categories = searchParams.get('categories')
  const collections = searchParams.get('collections')
  const discounts = searchParams.get('discounts')
  const colors = searchParams.get('colors')
  const sizes = searchParams.get('sizes')
  const sort = searchParams.get('sort')

  return {
    brands,
    categories,
    collections,
    colors,
    discounts,
    search,
    sizes,
    sort,
  }
}

interface ProductListProps extends ProductListFilterProps {
  brand?: Array<string>
  category?: string
  colors?: Array<string>
  type?: 'default' | 'special-price'
}

export function ProductList({
  brand,
  category,
  colors,
  showBrandFilter,
  showCategoryFilter,
  showCollectionFilter,
  showColorFilter,
  showDiscountFilter,
  showSizeFilter,
  type = 'default',
}: ProductListProps) {
  const { shopGridView } = useUserStore((state) => state)
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    content,
    featuredImage,
    featuredType,
    featuredVideo,
    lookBooks,
    page,
    pagination,
    products,
    setContent,
    setFeatured,
    setFilters,
    setLookBooks,
    setPage,
    setPagination,
    setProducts,
    setSelectedFilters,
  } = useProductListStore()

  const {
    brands,
    categories,
    colors: colorsParam,
    search,
    ...rest
  } = getFilterFromSearchParams(searchParams)

  const selectedBrands = brands ?? brand
  const selectedColors = colorsParam ?? colors
  const selectedCategory = categories ?? category

  const params = createURLParams({
    ...rest,
    brands: selectedBrands,
    categories: selectedCategory,
    colors: selectedColors,
    limit: shopGridView === 'three-col' ? 12 : 16,
    page,
    s: search,
  })

  const endpointUrl =
    type === 'special-price'
      ? endpoint.frontend.SPECIAL_PRICE_PRODUCTS
      : endpoint.frontend.PRODUCTS

  const { data, isLoading, isValidating } = useSWR<
    ProductListResponse,
    Error,
    string
  >(`${endpointUrl}?${params}`, getRequest)

  const handleStackProducts = useCallback(
    (data: ProductListResponse) => {
      if (data?.data) {
        const {
          content,
          data: products,
          featuredImage,
          featuredType,
          featuredVideo,
          pagination,
        } = data
        setFilters(data?.filters)
        setPagination(pagination)
        setProducts(products)
        setLookBooks(data?.lookBooks)
        setFeatured(featuredImage, featuredVideo, featuredType)
        setContent(content ?? '')
      }
    },
    [
      setFilters,
      setLookBooks,
      setPagination,
      setProducts,
      setFeatured,
      setContent,
    ],
  )

  useEffect(() => {
    if (data?.data) {
      handleStackProducts(data)
    }
  }, [data, handleStackProducts])

  useEffect(() => {
    const handleScroll = async () => {
      const windowScroll =
        window.innerHeight + document.documentElement.scrollTop
      const documentHeight =
        document.documentElement.offsetHeight - LOAD_MORE_OFFSET
      const isLoadMorePosition = windowScroll >= documentHeight

      if (
        isLoadMorePosition &&
        !isLoading &&
        pagination?.page < pagination?.pageCount
      ) {
        setPage(page + 1)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, page, pagination?.page, pagination?.pageCount, setPage])

  useEffect(() => {
    if (!searchParams) return

    const {
      brands,
      categories,
      collections,
      colors,
      discounts,
      search,
      sizes,
      sort,
    } = getFilterFromSearchParams(searchParams)
    setSelectedFilters({
      brands: brands ? brands.split(',') : [],
      categories: categories ? categories.split(',') : [],
      collections: collections ? collections.split(',') : [],
      colors: colors ? colors.split(',') : [],
      discounts: discounts ? discounts.split(',') : [],
      search: search ?? '',
      sizes: sizes ? sizes.split(',') : [],
      // @ts-expect-error - sort is not null
      sort: sort ?? Sort.Default,
    })
  }, [
    brand,
    category,
    searchParams,
    selectedBrands,
    selectedColors,
    selectedCategory,
    setSelectedFilters,
  ])

  const calculateLookBookIndices = (
    gridView: string,
    totalProducts: number,
  ): number[] => {
    const indices: number[] = []

    if (gridView === 'three-col') {
      const specificIndices = [3, 8, 11, 17]
      specificIndices.forEach((index) => {
        if (index < totalProducts) {
          indices.push(index)
        }
      })
    }

    if (gridView === 'four-col') {
      const specificIndices = [4, 10, 14, 20, 24, 30, 34, 40]
      specificIndices.forEach((index) => {
        if (index < totalProducts) {
          indices.push(index)
        }
      })
    }

    // if isMobileDevice then indices every 2 products
    if (isMobileDevice) {
      const specificIndices = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20]
      specificIndices.forEach((index) => {
        if (index < totalProducts) {
          indices.push(index)
        }
      })
    }

    // else {
    //     const interval = 5;
    //     const start = 3;
    //     for (let i = start; i < totalProducts; i += interval) {
    //         indices.push(i);
    //     }
    // }

    return indices
  }

  const mergeLookBooksToData = (
    products: Product[],
    lookBooks: LookBook[],
    gridView: string,
  ): RenderData[] => {
    const lookBookIndices = calculateLookBookIndices(gridView, products.length)
    const renderData: RenderData[] = []

    let lookBookIndex = 0
    products.forEach((product, index) => {
      if (lookBooks && lookBooks.length > 0) {
        if (lookBookIndices.includes(index) && lookBooks[lookBookIndex]) {
          renderData.push({ data: lookBooks[lookBookIndex], type: 'lookBook' })
          lookBookIndex++
        }
      }
      renderData.push({ data: product, type: 'product' })
    })

    return renderData
  }

  const renderProducts = () => {
    const renderData = mergeLookBooksToData(products, lookBooks, shopGridView)

    return renderData.map((data) => {
      if (data.type === 'lookBook') {
        return (
          <LookBookSlider
            key={data.data.id + '_' + shopGridView}
            {...data.data}
          />
        )
      }

      return (
        <ProductItem
          key={data.data.id + '_' + shopGridView}
          product={data.data}
        />
      )
    })
  }

  return (
    <div className="my-12">
      {content ||
      (featuredImage && featuredType === 'image') ||
      (featuredVideo && featuredType === 'video') ? (
        <div className="mb-10 grid grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-16">
          <div
            className={cn({
              'col-span-2': _.isEmpty(content),
            })}
          >
            {featuredType === 'image' && featuredImage ? (
              <img
                alt={featuredImage.alt}
                className="h-auto w-full"
                src={featuredImage.sizes[ImageSize.Large]}
                srcSet={featuredImage.srcset}
              />
            ) : null}
            {featuredType === 'video' && featuredVideo ? (
              <VideoPlayer
                autoplay
                hideControls
                loop
                muted
                videoType="mp4"
                videoUrl={featuredVideo}
              />
            ) : null}
          </div>
          {content && (
            <div
              className={cn('prose', {
                'col-span-2':
                  _.isEmpty(featuredImage) || _.isEmpty(featuredVideo),
              })}
              dangerouslySetInnerHTML={{ __html: content }}
            ></div>
          )}
        </div>
      ) : null}

      {isMobileDevice ? (
        <DrawerFilter
          showBrandFilter={showBrandFilter}
          showCategoryFilter={showCategoryFilter}
          showCollectionFilter={showCollectionFilter}
          showColorFilter={showColorFilter}
          showDiscountFilter={showDiscountFilter}
          showSizeFilter={showSizeFilter}
        />
      ) : (
        <ProductListFilter
          showBrandFilter={showBrandFilter}
          showCategoryFilter={showCategoryFilter}
          showCollectionFilter={showCollectionFilter}
          showColorFilter={showColorFilter}
          showDiscountFilter={showDiscountFilter}
          showSizeFilter={showSizeFilter}
        />
      )}

      {products.length === 0 && !isLoading && (
        <div className="flex h-[50vh] items-center justify-center">
          <div className="text-center">
            <ArchiveBoxIcon className="mx-auto mb-6 h-14 w-14" />
            <p className="uppercase">
              {__('No products found.', 'asavagroup')}
            </p>
          </div>
        </div>
      )}
      <div
        className={cn(`grid grid-cols-2 gap-x-4 gap-y-6`, {
          'md:grid-cols-3': shopGridView === 'three-col',
          'md:grid-cols-4': shopGridView === 'four-col',
        })}
      >
        {/* {lookBooks?.length > 0 &&
                    lookBooks.map((lookBook) => <LookBookSlider key={lookBook.id} {...lookBook} />)} */}
        {products && renderProducts()}
        {(isLoading || isValidating) && <ShopLoader />}
      </div>
    </div>
  )
}

type RenderData =
  | {
      data: LookBook
      type: 'lookBook'
    }
  | {
      data: Product
      type: 'product'
    }

export default ProductList
